import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon';
import Button from './Button';
import Input from './Input';
import Select from './Select';

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  amount, 
  currency = 'XAF', 
  title = 'Complete Payment',
  description,
  onPaymentSuccess,
  onPaymentError 
}) => {
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState('method'); // method, details, processing, success, error
  const [paymentData, setPaymentData] = useState({});
  const [errors, setErrors] = useState({});

  const paymentMethods = [
    {
      value: 'orange_money',
      label: 'Orange Money',
      icon: 'Smartphone',
      description: 'Pay with Orange Money mobile wallet',
      fees: '1.5%'
    },
    {
      value: 'mtn_momo',
      label: 'MTN Mobile Money',
      icon: 'Smartphone',
      description: 'Pay with MTN Mobile Money',
      fees: '1.5%'
    },
    {
      value: 'card',
      label: 'Credit/Debit Card',
      icon: 'CreditCard',
      description: 'Visa, Mastercard accepted',
      fees: '2.5%'
    },
    {
      value: 'bank_transfer',
      label: 'Bank Transfer',
      icon: 'Building',
      description: 'Direct bank transfer',
      fees: 'Free'
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setStep('method');
      setPaymentMethod('');
      setPaymentData({});
      setErrors({});
      setIsProcessing(false);
    }
  }, [isOpen]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const calculateFees = (method) => {
    const selectedMethod = paymentMethods?.find(m => m?.value === method);
    if (!selectedMethod || selectedMethod?.fees === 'Free') return 0;
    
    const feePercentage = parseFloat(selectedMethod?.fees?.replace('%', ''));
    return Math.round(amount * (feePercentage / 100));
  };

  const getTotalAmount = () => {
    return amount + calculateFees(paymentMethod);
  };

  const handleMethodSelect = (method) => {
    setPaymentMethod(method);
    setStep('details');
  };

  const validatePaymentData = () => {
    const newErrors = {};

    switch (paymentMethod) {
      case 'orange_money': case'mtn_momo':
        if (!paymentData?.phoneNumber) {
          newErrors.phoneNumber = 'Phone number is required';
        } else if (!/^6[0-9]{8}$/?.test(paymentData?.phoneNumber)) {
          newErrors.phoneNumber = 'Enter valid Cameroon phone number (6XXXXXXXX)';
        }
        break;
        
      case 'card':
        if (!paymentData?.cardNumber) {
          newErrors.cardNumber = 'Card number is required';
        } else if (!/^[0-9]{16}$/?.test(paymentData?.cardNumber?.replace(/\s/g, ''))) {
          newErrors.cardNumber = 'Enter valid 16-digit card number';
        }
        
        if (!paymentData?.expiryDate) {
          newErrors.expiryDate = 'Expiry date is required';
        } else if (!/^(0[1-9]|1[0-2])\/[0-9]{2}$/?.test(paymentData?.expiryDate)) {
          newErrors.expiryDate = 'Enter valid expiry date (MM/YY)';
        }
        
        if (!paymentData?.cvv) {
          newErrors.cvv = 'CVV is required';
        } else if (!/^[0-9]{3,4}$/?.test(paymentData?.cvv)) {
          newErrors.cvv = 'Enter valid CVV';
        }
        
        if (!paymentData?.cardholderName) {
          newErrors.cardholderName = 'Cardholder name is required';
        }
        break;
        
      case 'bank_transfer':
        if (!paymentData?.accountNumber) {
          newErrors.accountNumber = 'Account number is required';
        }
        if (!paymentData?.bankName) {
          newErrors.bankName = 'Bank name is required';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const processPayment = async () => {
    if (!validatePaymentData()) return;

    setIsProcessing(true);
    setStep('processing');

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Simulate random success/failure for demo
      const success = Math.random() > 0.2; // 80% success rate
      
      if (success) {
        setStep('success');
        if (onPaymentSuccess) {
          onPaymentSuccess({
            method: paymentMethod,
            amount: getTotalAmount(),
            transactionId: `TXN${Date.now()}`,
            timestamp: new Date()?.toISOString()
          });
        }
      } else {
        throw new Error('Payment failed. Please try again.');
      }
    } catch (error) {
      setStep('error');
      if (onPaymentError) {
        onPaymentError(error?.message);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPaymentData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const formatCardNumber = (value) => {
    return value?.replace(/\s/g, '')?.replace(/(.{4})/g, '$1 ')?.trim();
  };

  const formatExpiryDate = (value) => {
    return value?.replace(/\D/g, '')?.replace(/(\d{2})(\d)/, '$1/$2');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="w-full max-w-md bg-card rounded-xl shadow-floating animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-lg font-semibold text-foreground">{title}</h2>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            iconSize={20}
          />
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Amount Summary */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Amount</span>
              <span className="font-mono text-foreground">{formatAmount(amount)}</span>
            </div>
            {paymentMethod && calculateFees(paymentMethod) > 0 && (
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Processing Fee</span>
                <span className="font-mono text-muted-foreground">
                  {formatAmount(calculateFees(paymentMethod))}
                </span>
              </div>
            )}
            {paymentMethod && (
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="font-medium text-foreground">Total</span>
                <span className="font-mono font-semibold text-lg text-primary">
                  {formatAmount(getTotalAmount())}
                </span>
              </div>
            )}
          </div>

          {/* Step: Method Selection */}
          {step === 'method' && (
            <div className="space-y-3">
              <h3 className="font-medium text-foreground mb-4">Choose Payment Method</h3>
              {paymentMethods?.map((method) => (
                <button
                  key={method?.value}
                  onClick={() => handleMethodSelect(method?.value)}
                  className="w-full flex items-center justify-between p-4 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-smooth"
                >
                  <div className="flex items-center space-x-3">
                    <Icon name={method?.icon} size={20} className="text-primary" />
                    <div className="text-left">
                      <div className="font-medium text-foreground">{method?.label}</div>
                      <div className="text-sm text-muted-foreground">{method?.description}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-foreground">{method?.fees}</div>
                    <Icon name="ChevronRight" size={16} className="text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step: Payment Details */}
          {step === 'details' && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep('method')}
                  iconName="ArrowLeft"
                  iconSize={16}
                />
                <h3 className="font-medium text-foreground">
                  {paymentMethods?.find(m => m?.value === paymentMethod)?.label}
                </h3>
              </div>

              {/* Mobile Money Forms */}
              {(paymentMethod === 'orange_money' || paymentMethod === 'mtn_momo') && (
                <div className="space-y-4">
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="6XXXXXXXX"
                    value={paymentData?.phoneNumber || ''}
                    onChange={(e) => handleInputChange('phoneNumber', e?.target?.value)}
                    error={errors?.phoneNumber}
                    required
                    description="Enter your mobile money phone number"
                  />
                  
                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
                    <div className="flex items-start space-x-2">
                      <Icon name="Info" size={16} className="text-accent mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium text-accent">Payment Instructions</p>
                        <p className="text-muted-foreground mt-1">
                          You will receive a payment request on your phone. 
                          Enter your PIN to complete the transaction.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Card Form */}
              {paymentMethod === 'card' && (
                <div className="space-y-4">
                  <Input
                    label="Card Number"
                    type="text"
                    placeholder="1234 5678 9012 3456"
                    value={paymentData?.cardNumber || ''}
                    onChange={(e) => handleInputChange('cardNumber', formatCardNumber(e?.target?.value))}
                    error={errors?.cardNumber}
                    maxLength={19}
                    required
                  />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Expiry Date"
                      type="text"
                      placeholder="MM/YY"
                      value={paymentData?.expiryDate || ''}
                      onChange={(e) => handleInputChange('expiryDate', formatExpiryDate(e?.target?.value))}
                      error={errors?.expiryDate}
                      maxLength={5}
                      required
                    />
                    <Input
                      label="CVV"
                      type="text"
                      placeholder="123"
                      value={paymentData?.cvv || ''}
                      onChange={(e) => handleInputChange('cvv', e?.target?.value?.replace(/\D/g, ''))}
                      error={errors?.cvv}
                      maxLength={4}
                      required
                    />
                  </div>
                  
                  <Input
                    label="Cardholder Name"
                    type="text"
                    placeholder="John Doe"
                    value={paymentData?.cardholderName || ''}
                    onChange={(e) => handleInputChange('cardholderName', e?.target?.value)}
                    error={errors?.cardholderName}
                    required
                  />
                </div>
              )}

              {/* Bank Transfer Form */}
              {paymentMethod === 'bank_transfer' && (
                <div className="space-y-4">
                  <Select
                    label="Bank Name"
                    placeholder="Select your bank"
                    options={[
                      { value: 'bicec', label: 'BICEC' },
                      { value: 'sgbc', label: 'Société Générale' },
                      { value: 'uba', label: 'UBA Cameroon' },
                      { value: 'ecobank', label: 'Ecobank' },
                      { value: 'afriland', label: 'Afriland First Bank' }
                    ]}
                    value={paymentData?.bankName || ''}
                    onChange={(value) => handleInputChange('bankName', value)}
                    error={errors?.bankName}
                    required
                  />
                  
                  <Input
                    label="Account Number"
                    type="text"
                    placeholder="Enter your account number"
                    value={paymentData?.accountNumber || ''}
                    onChange={(e) => handleInputChange('accountNumber', e?.target?.value)}
                    error={errors?.accountNumber}
                    required
                  />
                </div>
              )}

              <Button
                variant="default"
                fullWidth
                onClick={processPayment}
                disabled={isProcessing}
                iconName="CreditCard"
                iconPosition="left"
                iconSize={16}
              >
                Pay {formatAmount(getTotalAmount())}
              </Button>
            </div>
          )}

          {/* Step: Processing */}
          {step === 'processing' && (
            <div className="text-center py-8">
              <Icon name="Loader2" size={48} className="mx-auto text-primary animate-spin mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Processing Payment</h3>
              <p className="text-muted-foreground">Please wait while we process your payment...</p>
              {(paymentMethod === 'orange_money' || paymentMethod === 'mtn_momo') && (
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 mt-4">
                  <p className="text-sm text-accent">
                    Check your phone for the payment request
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Check" size={32} className="text-success" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Payment Successful!</h3>
              <p className="text-muted-foreground mb-4">
                Your payment of {formatAmount(getTotalAmount())} has been processed successfully.
              </p>
              <Button
                variant="default"
                fullWidth
                onClick={onClose}
              >
                Continue
              </Button>
            </div>
          )}

          {/* Step: Error */}
          {step === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-error/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="X" size={32} className="text-error" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Payment Failed</h3>
              <p className="text-muted-foreground mb-4">
                There was an issue processing your payment. Please try again.
              </p>
              <div className="space-y-2">
                <Button
                  variant="default"
                  fullWidth
                  onClick={() => setStep('details')}
                >
                  Try Again
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;